import { Inject, Injectable, NgZone } from '@angular/core';
import { DOCUMENT } from '@angular/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { FocusTrap, FocusTrapFactory } from '@angular/cdk/a11y';
import { Subscription } from 'rxjs';

interface BackgroundElementState {
  element: HTMLElement;
  ariaHidden: string | null;
  inert: boolean;
}

/**
 * Centraliza la accesibilidad de los modales ngx-bootstrap:
 * trampa de foco (CDK), inert/aria-hidden en el fondo y restauración del foco.
 *
 * El trap se activa observando el DOM (`modal-container`), no solo los eventos
 * de BsModalService, para que funcione aunque haya más de una instancia del servicio.
 *
 * CDK FocusTrap inserta anclas como hermanos del contenedor (hijos de body).
 * El observer ignora esos nodos y no recrea el trap si el contenedor no cambia,
 * para evitar un bucle de mutaciones.
 */
@Injectable({ providedIn: 'root' })
export class ModalAccessibilityService {
  private started = false;
  private subscriptions = new Subscription();
  private focusTrap: FocusTrap | null = null;
  private trappedContainer: HTMLElement | null = null;
  private previouslyFocusedElement: HTMLElement | null = null;
  private backgroundElements: BackgroundElementState[] = [];
  private mutationObserver: MutationObserver | null = null;
  private modalOpen = false;
  private readonly onFocusIn = (event: FocusEvent) => this.captureOutsideFocus(event);

  constructor(
    private bsModalService: BsModalService,
    private focusTrapFactory: FocusTrapFactory,
    private ngZone: NgZone,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  init(): void {
    if (this.started) {
      return;
    }

    this.started = true;
    this.document.addEventListener('focusin', this.onFocusIn, true);

    this.subscriptions.add(this.bsModalService.onShown.subscribe(() => this.syncModalState()));

    this.subscriptions.add(this.bsModalService.onHidden.subscribe(() => this.syncModalState()));

    this.ngZone.runOutsideAngular(() => {
      this.mutationObserver = new MutationObserver((mutations) => {
        if (!this.hasRelevantModalMutation(mutations)) {
          return;
        }

        this.ngZone.run(() => this.syncModalState());
      });
      this.mutationObserver.observe(this.document.body, { childList: true });
    });
  }

  destroy(): void {
    this.subscriptions.unsubscribe();
    this.mutationObserver?.disconnect();
    this.mutationObserver = null;
    this.document.removeEventListener('focusin', this.onFocusIn, true);
    this.destroyFocusTrap();
    this.restoreBackground();
    this.previouslyFocusedElement = null;
    this.trappedContainer = null;
    this.started = false;
  }

  private syncModalState(): void {
    const container = this.getTopModalContainer();

    if (!container) {
      if (this.modalOpen) {
        this.modalOpen = false;
        this.trappedContainer = null;
        this.destroyFocusTrap();
        this.restoreBackground();
        this.restoreFocus();
      }
      return;
    }

    if (this.modalOpen && this.trappedContainer === container) {
      return;
    }

    this.modalOpen = true;
    this.trappedContainer = container;
    this.isolateBackground();
    this.setupFocusTrap(container);
    this.focusInitialElement(container);
  }

  private hasRelevantModalMutation(mutations: MutationRecord[]): boolean {
    return mutations.some(
      (mutation) =>
        this.hasModalLayerNode(mutation.addedNodes) ||
        this.hasModalLayerNode(mutation.removedNodes),
    );
  }

  private hasModalLayerNode(nodes: NodeList): boolean {
    return Array.from(nodes).some(
      (node) =>
        node instanceof HTMLElement && this.isModalLayer(node) && !this.isFocusTrapAnchor(node),
    );
  }

  private captureOutsideFocus(event: FocusEvent): void {
    const target = event.target;

    if (!(target instanceof HTMLElement) || target.closest('modal-container')) {
      return;
    }

    this.previouslyFocusedElement = target;
  }

  private getTopModalContainer(): HTMLElement | null {
    const containers = this.document.querySelectorAll('modal-container');

    if (!containers.length) {
      return null;
    }

    return containers[containers.length - 1] as HTMLElement;
  }

  private setupFocusTrap(container: HTMLElement): void {
    this.destroyFocusTrap();

    this.focusTrap = this.focusTrapFactory.create(container, true);
    this.focusTrap.attachAnchors();
    this.focusTrap.enabled = true;
  }

  private destroyFocusTrap(): void {
    if (this.focusTrap) {
      this.focusTrap.destroy();
      this.focusTrap = null;
    }
  }

  private focusInitialElement(container: HTMLElement): void {
    const applyFocus = () => {
      if (!this.getTopModalContainer()) {
        return;
      }

      const title = container.querySelector<HTMLElement>(
        [
          'h2.modal__title[tabindex="-1"]',
          'h2.modal__title',
          'h2[tabindex="-1"]',
          '#info-modal-title',
          '#otp-modal-title',
          '#confirm-modal-title',
          '#notification-modal-title',
          '#modal-error-title',
          '#conditions-info-modal-title',
          '#change-password-modal-title',
        ].join(', '),
      );

      if (title) {
        if (!title.hasAttribute('tabindex')) {
          title.setAttribute('tabindex', '-1');
        }

        title.focus({ preventScroll: true });
        return;
      }

      const preferredControl = container.querySelector<HTMLElement>(
        [
          'input:not([type="hidden"]):not([disabled])',
          'textarea:not([disabled])',
          'select:not([disabled])',
          '.modal__btn.btn--primary',
          '.btn--primary.modal__btn',
          '.modal__btn--conditions-cta',
        ].join(', '),
      );

      if (preferredControl) {
        preferredControl.focus({ preventScroll: true });
        return;
      }

      this.focusFirstTabbableExcludingClose(container);
    };

    // 0ms: foco inicial. 120ms: corrige carreras (autofocus / ngAfterViewInit en la X).
    setTimeout(applyFocus, 0);
    setTimeout(() => {
      const active = this.document.activeElement;
      if (
        active instanceof HTMLElement &&
        container.contains(active) &&
        (active.classList.contains('modal__close') || active.closest('.modal__close'))
      ) {
        applyFocus();
      }
    }, 120);
  }

  /** Evita que el fallback del trap deje el anillo en la X al abrir. */
  private focusFirstTabbableExcludingClose(container: HTMLElement): void {
    const candidates = Array.from(
      container.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ).filter((el) => !el.classList.contains('modal__close') && !el.closest('.modal__close'));

    const first = candidates[0];
    if (first) {
      first.focus({ preventScroll: true });
      return;
    }

    this.focusTrap?.focusFirstTabbableElementWhenReady();
  }

  private isolateBackground(): void {
    this.restoreBackground();

    Array.from(this.document.body.children).forEach((node) => {
      if (
        !(node instanceof HTMLElement) ||
        this.isModalLayer(node) ||
        this.isFocusTrapAnchor(node)
      ) {
        return;
      }

      this.backgroundElements.push({
        element: node,
        ariaHidden: node.getAttribute('aria-hidden'),
        inert: this.getInert(node),
      });

      node.setAttribute('aria-hidden', 'true');
      this.setInert(node, true);
    });
  }

  private restoreBackground(): void {
    for (const { element, ariaHidden, inert } of this.backgroundElements) {
      if (ariaHidden === null) {
        element.removeAttribute('aria-hidden');
      } else {
        element.setAttribute('aria-hidden', ariaHidden);
      }

      this.setInert(element, inert);
    }

    this.backgroundElements = [];
  }

  private restoreFocus(): void {
    const element = this.previouslyFocusedElement;
    this.previouslyFocusedElement = null;

    if (!element || typeof element.focus !== 'function' || !element.isConnected) {
      return;
    }

    setTimeout(() => element.focus({ preventScroll: true }), 0);
  }

  private isModalLayer(element: HTMLElement): boolean {
    const tagName = element.tagName.toLowerCase();
    return tagName === 'modal-container' || element.classList.contains('modal-backdrop');
  }

  private isFocusTrapAnchor(element: HTMLElement): boolean {
    return element.classList.contains('cdk-focus-trap-anchor');
  }

  private getInert(element: HTMLElement): boolean {
    return 'inert' in element
      ? Boolean((element as HTMLElement & { inert: boolean }).inert)
      : false;
  }

  private setInert(element: HTMLElement, value: boolean): void {
    if ('inert' in element) {
      (element as HTMLElement & { inert: boolean }).inert = value;
    }
  }
}
